/*** IMPORTS FROM imports-loader ***/
var define = false;

"use strict";

var ko = require("knockout");

var searchQuery = require("../searchquery");
var stringUtil = require("../util/string");

var AdvancedSearchItemViewModel = require("./advancedSearchItemViewModel");
var DefaultAdvancedSearchItem = require("./defaultAdvancedSearchItem");
var shared = require("./shared");



//~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
// Private
//~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~

//---------------------------------------
// Creates the picklist data based on the
// properties specified for the advancedSearch.
function buildPropertyPicklistData(properties, excludeAnything) {
    var id, items = [];
    if (!excludeAnything) {
        items.push({ name: "Anything", id: "" });
    }
    for (id in properties) {
        if (properties.hasOwnProperty(id)) {
            items.push({ name: properties[id].name, id: id });
        }
    }
    return items;
}


//-------------------------------------------
// Converts the parsed search query to advancedSearch
// items.  The view-model currently does not
// handle `or` binary expressions or nested
// expressions.
function load(advancedSearch, excludeAnything, result, stop) {
    if (result !== null && typeof result === "object") {
        switch (result.type) {
        case "default":
            return excludeAnything ? null : [new AdvancedSearchItemViewModel(advancedSearch, "", result.phrase, false)];
        case "property":
            return [new AdvancedSearchItemViewModel(advancedSearch, result.property.toLowerCase(), result.phrase, false)];
        case "not":
            var not = result.children[0];
            if (not.type === "property" || not.type === "default") {
                return [new AdvancedSearchItemViewModel(advancedSearch, not.property.toLowerCase(), not.phrase, true)];
            }
            break;
        case "and":
            if (!stop) {
                var i, l, and = [], child;
                for (i = 0, l = result.children.length; i < l; i += 1) {
                    child = load(advancedSearch, excludeAnything, result.children[i], true);
                    if (child === null) { return null; }
                    and = and.concat(child);
                }
                return and;
            }
            break;
        }
    }
    return null;
}


//-------------------------------------------
// Parses the specified search phrase.
function parse(advancedSearch, excludeAnything, phrase) {
    var defaultValue = [new AdvancedSearchItemViewModel(advancedSearch)];
    if (stringUtil.isNullOrWhitespace(phrase)) { return { phrase: defaultValue, supportedQuery: true }; }
    var results = load(advancedSearch, excludeAnything, searchQuery.parse(phrase));
    return (Array.isArray(results) && results.length > 0)
        ? { phrase: results, supportedQuery: true }
        : { phrase: defaultValue, supportedQuery: false };
}


//-------------------------------------------
// Converts the items to the string representation.
function save(items, supportedQuery, phrase) {
    if (!supportedQuery) {
        return phrase;
    }
    var result = "";
    if (Array.isArray(items) && items.length > 0) {
        var i, l, item, results = [];
        for (i = 0, l = items.length; i < l; i += 1) {
            item = items[i];
            if (item instanceof AdvancedSearchItemViewModel) {
                item = item.toSearchQuery();
                if (item !== null) { results.push(item); }
            }
        }
        if (results.length === 1) {
            result = results[0].toString();
        } else if (results.length > 1) {
            result = searchQuery.buildBinary("and", results).toString();
        }
    }
    return result;
}



//~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
// AdvancedSearch view-model
//~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~

//-------------------------------------------
function AdvancedSearchViewModel(config) {
    if (!(this instanceof AdvancedSearchViewModel)) { return new AdvancedSearchViewModel(config); } // if new was not used, force it now

    // validate and normalize config.
    config = config || {};
    var hidden = (typeof config.hidden !== "boolean" ? true : !!config.hidden);
    var phrase = (typeof config.phrase === "string" ? config.phrase : "");
    var excludeAnything = (typeof config.excludeAnything === "boolean" ? config.excludeAnything : false);
    var hideOnReset = (typeof config.hideOnReset === "boolean" ? config.hideOnReset : false);
    var id, lower, property, properties = config.properties || {};
    this._excludeAnything = excludeAnything;
    this._properties = {};
    for (id in properties) {
        if (properties.hasOwnProperty(id)) {
            lower = id.toLowerCase();
            property = properties[id] || {};
            if (typeof property.viewModel !== "function") { throw new Error("properties.viewModel must be a function."); }
            this._properties[lower] = { name: property.name || id, data: property.data, id: id, viewModel: property.viewModel };
        }
    }
    this.options = buildPropertyPicklistData(this._properties, excludeAnything);

    // initialize
    this.hidden = ko.observable(hidden);
    this.phrase = ko.observable(phrase);
    this._internalUpdate = false;
    this._hideOnReset = hideOnReset;
    this.items = ko.observableArray();
    this.supportedQuery = ko.observable(false);

    // bound functions
    this.add = AdvancedSearchViewModel.prototype.add.bind(this);
    this.remove = AdvancedSearchViewModel.prototype.remove.bind(this);
    this.reset = AdvancedSearchViewModel.prototype.reset.bind(this);
    this.toggle = AdvancedSearchViewModel.prototype.toggle.bind(this);

    // subscriptions
    this._items = ko.computed(this._itemsChanged, this);
    this._hiddenSubscription = this.hidden.subscribe(this._phraseChanged, this);
    this._phraseSubscription = this.phrase.subscribe(this._phraseChanged, this);

    // trigger parsing of initial phrase and update of items & supportedQuery (if not hidden)
    this.phrase.valueHasMutated();
}


//-------------------------------------------
AdvancedSearchViewModel.prototype.add = function advancedSearchViewModelAdd() {
    this.items.push(new AdvancedSearchItemViewModel(this, undefined, undefined, undefined, true));
    return false;
};


//-------------------------------------------
AdvancedSearchViewModel.prototype.remove = function advancedSearchViewModelRemove(item) {
    var items = this.items.peek();
    if (Array.isArray(items) && items.length > 1) {
        var removedItems = this.items.remove(item), len = removedItems.length, i;
        for (i = 0; i < len; i += 1) {
            removedItems[i].dispose();
        }
    } else {
        this.reset();
        this.hidden(true);
    }
    return false;
};


//-------------------------------------------
AdvancedSearchViewModel.prototype.reset = function advancedSearchViewModelReset() {
    this.phrase("");
    var item = this.items.peek();
    item = item && item[0];
    if (typeof item.property === "function") { item.property(""); }
    if (this._hideOnReset) { this.hidden(true); }
    return false;
};


//-------------------------------------------
AdvancedSearchViewModel.prototype.toggle = function advancedSearchViewModelToggle() {
    this.hidden(!this.hidden.peek());
    return false;
};


//-------------------------------------------
// Refreshes the search phrase when anything
// within the items is updated.
AdvancedSearchViewModel.prototype._itemsChanged = shared.createInternalSubscription(
    "items",
    shared.createInternalUpdateGuard(
        function advancedSearchViewModelItemsComputed() {
            this.phrase(save(this.items(), this.supportedQuery.peek(), this.phrase.peek()));
        }
    )
);


//-------------------------------------------
// The function is passed to the advancedSearch item
// used to lookup the property type.
AdvancedSearchViewModel.prototype.lookupPropertyType = function advancedSearchViewModelLookupPropertyType(id) {
    id = (typeof id === "string" ? id.toLowerCase() : "default");
    var property = this._properties[id];
    return property
        ? { data: property.data, id: id, viewModel: property.viewModel }
        : { id: id, viewModel: DefaultAdvancedSearchItem };
};


//-------------------------------------------
// Reloads the `items` if either the
// `phrase` or `hidden` observables have changed.
AdvancedSearchViewModel.prototype._phraseChanged = shared.createInternalUpdateGuard(
    function advancedSearchViewModelPhraseChanged() {
        if (!this.hidden.peek()) {
            var parsed = parse(this, this._excludeAnything, this.phrase.peek());
            this.items(parsed.phrase);
            this.supportedQuery(parsed.supportedQuery);
        }
    }
);

module.exports = AdvancedSearchViewModel;




//////////////////
// WEBPACK FOOTER
// ./~/pli/advancedsearch/advancedSearchViewModel.js
// module id = 454
// module chunks = 49